import { Image } from '@tiptap/extension-image';

const IMAGE_WIDTH_MIN_PX = 60;

type ResizeDirection = -1 | 1;

/**
 * The support editors' image node with corner drag handles for resizing. The chosen size
 * persists as the `width` attribute (px) — the only sizing the support HTML sanitizer lets
 * through — so a resize survives the round trip to the server and back into the editor.
 * Height stays automatic, preserving the aspect ratio.
 */
export const ResizableImage = Image.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			width: {
				default: null,
				parseHTML: (element: HTMLElement) => {
					const value = Number.parseInt(element.getAttribute('width') ?? '', 10);
					return Number.isFinite(value) && value > 0 ? value : null;
				},
				renderHTML: (attributes: Record<string, unknown>) => (attributes.width ? { width: attributes.width } : {}),
			},
		};
	},

	addNodeView() {
		return ({ node, editor, getPos }) => {
			const dom = document.createElement('div');
			dom.className = 'lev-resizable-image';
			const img = document.createElement('img');
			dom.appendChild(img);

			const applyNodeAttributes = (current: typeof node) => {
				img.src = current.attrs.src ?? '';
				if (current.attrs.alt) {
					img.alt = current.attrs.alt;
				} else {
					img.removeAttribute('alt');
				}
				if (current.attrs.title) {
					img.title = current.attrs.title;
				} else {
					img.removeAttribute('title');
				}
				img.style.width = current.attrs.width ? `${current.attrs.width}px` : '';
			};
			applyNodeAttributes(node);

			const commitWidth = (width: number) => {
				if (editor.isDestroyed) return;
				const pos = getPos();
				if (typeof pos !== 'number') return;
				const current = editor.state.doc.nodeAt(pos);
				if (!current) return;
				editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, width }));
			};

			// Live-resize the DOM while dragging; commit the width as a single transaction (one
			// undo step) on release. The node view's destroy() detaches an in-flight drag so the
			// window listeners cannot outlive the view (image deleted or editor unmounted mid-drag).
			let detachActiveDrag: (() => void) | null = null;
			const startResize = (event: MouseEvent, directionX: ResizeDirection) => {
				event.preventDefault();
				event.stopPropagation();
				const startX = event.clientX;
				const startWidth = img.offsetWidth;
				const widthMax = Math.max(IMAGE_WIDTH_MIN_PX, editor.view.dom.clientWidth);
				let width = startWidth;
				const onMouseMove = (moveEvent: MouseEvent) => {
					const delta = directionX * (moveEvent.clientX - startX);
					width = Math.min(widthMax, Math.max(IMAGE_WIDTH_MIN_PX, startWidth + delta));
					img.style.width = `${width}px`;
				};
				const detach = () => {
					window.removeEventListener('mousemove', onMouseMove);
					window.removeEventListener('mouseup', onMouseUp);
					detachActiveDrag = null;
				};
				const onMouseUp = () => {
					detach();
					commitWidth(Math.round(width));
				};
				detachActiveDrag?.();
				detachActiveDrag = detach;
				window.addEventListener('mousemove', onMouseMove);
				window.addEventListener('mouseup', onMouseUp);
			};

			for (const corner of ['nw', 'ne', 'sw', 'se'] as const) {
				const handle = document.createElement('span');
				handle.className = `lev-image-handle lev-image-handle--${corner}`;
				// Left-edge handles grow the image when dragged left, right-edge ones when dragged right.
				const direction: ResizeDirection = corner === 'ne' || corner === 'se' ? 1 : -1;
				handle.addEventListener('mousedown', mouseDownEvent => startResize(mouseDownEvent, direction));
				dom.appendChild(handle);
			}

			return {
				dom,
				update: updatedNode => {
					if (updatedNode.type !== node.type) return false;
					applyNodeAttributes(updatedNode);
					return true;
				},
				destroy: () => {
					detachActiveDrag?.();
				},
			};
		};
	},
});
